import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Box, Button, TextField, Typography, Modal } from "@mui/material";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useNavigate, useLocation } from "react-router-dom";
import { Flag, FlashOn, Comment, Cancel, PriorityHigh, Undo } from "@mui/icons-material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import HanziCharacter from "../../utils/HanziWriter";
import ModalUpp from "../../utils/ModalUpp";
import RenderBadges from "../../utils/RenderBadges";

const Quiz = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cardLoading, setCardLoading] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizMeaning, setQuizMeaning] = useState("");
  const [quizScore, setQuizScore] = useState(0);
  const [partsData, setPartsData] = useState([]);
  const [subPartsData, setSubPartsData] = useState([]);
  const [usesCharData, setUsesCharData] = useState([]);
  const [usesMCWData, setUsesMCWData] = useState([]);
  const [phrasesData, setPhrasesData] = useState([]);
  const [dyzData, setDyzData] = useState([]);
  const [paronymData, setParonymData] = useState([]);
  const [simSoundData, setSimSoundData] = useState([]);
  const [simSoundModalOpen, setSimSoundModalOpen] = useState(false);
  const [modalUppOpen, setModalUppOpen] = useState(false);
  const [modalUppData, setModalUppData] = useState(null);
  const [modalPartsData, setModalPartsData] = useState([]);
  const [modalSubPartsData, setModalSubPartsData] = useState([]);
  const [modalUsesCharData, setModalUsesCharData] = useState([]);
  const [modalUsesMCWData, setModalUsesMCWData] = useState([]);
  const [modalPhrasesData, setModalPhrasesData] = useState([]);
  const [modalDyzData, setModalDyzData] = useState([]);
  const [modalParonymData, setModalParonymData] = useState([]);
  const [renderKey, setRenderKey] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const flippedRef = useRef(false);
  const [flippedOnce, setFlippedOnce] = useState(false);
  const [pinyinInput, setPinyinInput] = useState("");
  const [pinyinLetterMatch, setPinyinLetterMatch] = useState(false);
  const [pinyinMatch, setPinyinMatch] = useState(false);
  const pinyinTextFieldRef = useRef(null); // Allow focus at each render
  const [flagId, setFlagId] = useState(0);
  const [flagFlag, setFlagFlag] = useState(0); // To toggle flagging mode
  const [flagging, setFlagging] = useState(false); // To toggle flagging mode
  const [flagNote, setFlagNote] = useState("");
  const flagTextFieldRef = useRef(null); // Allow focus after flagging
  // const [discussId, setDiscussId] = useState(0);
  // const [discussNotify, setDiscussNotify] = useState(0);
  // const [discussing, setDiscussing] = useState(false); // Toggle discussing mode
  // const [discussNote, setDiscussNote] = useState("");
  // const discussTextFieldRef = useRef(null); // Allow focus after discussging
  const [commenting, setCommenting] = useState(false);
  const [hanziNote, setHanziNote] = useState("");
  const [meaningHint, setMeaningHint] = useState("");
  const [showHint, setShowHint] = useState(false);
  const hanziNoteRef = useRef(null);
  const meaningHintRef = useRef(null);
  const [priorityId, setPriorityId] = useState(0);
  const [showPriorityActions, setShowPriorityActions] = useState(false);
  const [enableAnswerBoxes, setEnableAnswerBoxes] = useState(false);
  const [disableAnimation, setDisableAnimation] = useState(false);
  const audioPlayed = useRef(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [extraSoundAnswersData, setExtraSoundAnswersData] = useState([]);
  const [incorrectSoundAttempts, setIncorrectSoundAttempts] = useState(0);
  const [selectedCorrectSoundAnswer, setSelectedCorrectSoundAnswer] = useState(null);
  const [incorrectSoundAnswers, setIncorrectSoundAnswers] = useState([]);
  const [allSoundAnswerOptions, setAllSoundAnswerOptions] = useState([]);
  const [shuffledSoundAnswers, setShuffledSoundAnswers] = useState([]);
  const [soundScore, setSoundScore] = useState(6);
  const [meaHanIndex, setMeaHanIndex] = useState(0);
  const [remainingTime, setRemainingTime] = useState(currentUser.timer || 20);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  const { quizCat: rawQuizCat, quizType } = location.state || {};
  const quizCat = rawQuizCat?.endsWith(".0") ? rawQuizCat.replace(/\.0$/, "") : rawQuizCat;
  const clickMode = currentUser.clickMode;
  const isSoundChoiceQuiz = quizType === "PinHan" || quizType === "MeaHan";
  const isListenPinyinQuiz = quizType === "LisPin";
  const shouldAutoPlayOnQuestionLoad = quizType === "PinHan" || quizType === "LisPin";
  // console.log("clickMode: ", clickMode);
  const cardFace = currentUser.cardFace;
  // console.log("currentUser: ", currentUser);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    // Countdown timer logic
    if (currentUser.timer === 0) return; // Skip timer logic if timer is disabled
    if (remainingTime === 0) return; // If the timer is at 0, prevent creating a new interval
    const timer = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime > 0) return prevTime - 1;
        clearInterval(timer); // Stop the timer when it reaches 0
        return 0;
      });
    }, 1000);
    return () => clearInterval(timer); // Cleanup on unmount
  }, [remainingTime, currentUser.timer]);

  const restartTimer = () => {
    setRemainingTime(currentUser.timer);
  };
  // Function to format time with leading zero
  const formatTime = (time) => (time < 10 ? `0${time}` : time);
  // Determine timer color based on remaining time
  const getTimerColor = () => {
    if (remainingTime < 5) return "red";
    if (remainingTime < 10) return "orange";
    return "inherit"; // Default color
  };

  const soundAnswerCharacter = useMemo(() => {
    if (quizQuestions.length === 0) return null;
    if (quizQuestions[0].hanzi_type === 1) {
      setMeaHanIndex(1);
      return quizQuestions[0].hanzi_character.charAt(0); // Use first character
    } else if (quizQuestions[0].hanzi_character.length > 1) {
      const characters = quizQuestions[0].hanzi_character.split("");
      const randomIndex = Math.floor(Math.random() * characters.length);
      setMeaHanIndex(randomIndex + 1);
      return characters[randomIndex]; // Use a random character
    }
    setMeaHanIndex(1);
    return quizQuestions[0].hanzi_character; // Use the full character if single
  }, [quizQuestions]);

  const pinyinWithHighlight = useMemo(() => {
    // console.log("206 soundAnswerCharacter: ", soundAnswerCharacter);
    if (quizQuestions.length === 0) return [];
    if (quizQuestions[0].hanzi_type === 1 || quizQuestions[0].hanzi_character.length === 1) {
      return [{ text: quizQuestions[0].hanzi_pinyin, bold: false }]; // No highlight needed
    } else {
      const characters = quizQuestions[0].hanzi_character.split("");
      const pinyins = quizQuestions[0].hanzi_pinyin.split(" ");
      const index = characters.indexOf(soundAnswerCharacter); // Find the index of the soundAnswerCharacter
      return pinyins.map((pinyin, i) => ({
        text: pinyin,
        bold: i === index, // Highlight the pinyin if its index matches the character
      }));
    }
  }, [quizQuestions, soundAnswerCharacter]);

  const handleNewSoundQuestion = useCallback(() => {
    if (
      !isSoundChoiceQuiz ||
      quizQuestions.length === 0 ||
      // audioPlayed.current ||
      allSoundAnswerOptions.length === 0
    ) {
      return; // Exit if conditions are not met
    }
    // console.log("handleNewSoundQuestion useCallback: ", quizQuestions[0]?.hanzi_character);
    // Find the corresponding pinyin
    const correctPinyin = (() => {
      if (quizQuestions[0].hanzi_type === 1 || quizQuestions[0].hanzi_character.length === 1) {
        return quizQuestions[0].hanzi_pinyin; // Use the full pinyin
      } else {
        const characters = quizQuestions[0].hanzi_character.split("");
        const pinyins = quizQuestions[0].hanzi_pinyin.split(" ");
        const index = characters.indexOf(soundAnswerCharacter); // Find the index of the character answer
        return pinyins[index] || ""; // Use same index for pinyin as character, empty string if not found
      }
    })();

    let soundIncorrectList = Array.from(
      new Set(
        allSoundAnswerOptions
          .filter((q) => {
            // Exclude quizQuestions that include the soundAnswerCharacter in their hanzi_character string
            return (
              !q.hanzi_character.includes(soundAnswerCharacter) && q.hanzi_pinyin !== correctPinyin
            );
          })
          .flatMap((q) => {
            const characters =
              q.hanzi_type === 1
                ? [q.hanzi_character.charAt(0)] // Single character
                : q.hanzi_character.length > 1
                  ? q.hanzi_character.split("") // Split into individual characters
                  : [q.hanzi_character]; // Treat as a single character if length <= 1
            // Return characters excluding any that match the soundAnswerCharacter
            return characters.filter((char) => char !== soundAnswerCharacter);
          }),
      ),
    ).slice(0, 17); // Limit to 17 alternate answers

    // If soundIncorrectList has fewer than 17 values, add random characters from extraSoundAnswersData
    if (soundIncorrectList.length < 17 && extraSoundAnswersData.length > 0) {
      const availableChars = extraSoundAnswersData
        .map((item) => item.hanzi_character)
        .filter((char) => char !== soundAnswerCharacter && !soundIncorrectList.includes(char));

      const shuffledAvailable = [...availableChars].sort(() => Math.random() - 0.5);
      const needed = 17 - soundIncorrectList.length;
      soundIncorrectList = [...soundIncorrectList, ...shuffledAvailable.slice(0, needed)];
    }
    console.log("160 correctPinyin:", correctPinyin);
    console.log("Incorrect Answers:", soundIncorrectList);
    const totalBoxes =
      soundIncorrectList.length >= 17 ? 18 : soundIncorrectList.length >= 11 ? 12 : 6;
    const tempSoundList = soundIncorrectList.slice(0, totalBoxes - 1);
    // Commented below line out on 4/27/25 when I implemented improved shuffle (~18 additional lines below)
    // const answers = [...tempSoundList, soundAnswerCharacter].sort(() => Math.random() - 0.5);
    const shuffleArray = (array) => {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    };
    // Create answers array and shuffle multiple times
    let answers = [...tempSoundList];
    answers = shuffleArray(answers);
    // Random insertion of correct answer
    const randomIndex = Math.floor(Math.random() * (answers.length + 1));
    answers.splice(randomIndex, 0, soundAnswerCharacter);
    console.log(
      "answers.length:",
      answers.length,
      "totalBoxes:",
      totalBoxes,
      "quizType:",
      quizType,
    );
    // Verify we have the correct number of answers
    if (answers.length !== totalBoxes) {
      console.warn(`Expected ${totalBoxes} answers but got ${answers.length}`);
    }
    const shuffledAnswers = answers.map((char) => {
      const matched = allSoundAnswerOptions.find((q) => q.hanzi_character.includes(char));
      return {
        character: char,
        pinyin: char === soundAnswerCharacter ? correctPinyin : matched?.hanzi_pinyin,
        type: matched?.hanzi_type ?? 1,
      };
    });
    console.log("shuffledSoundAnswers:", shuffledAnswers);
    setShuffledSoundAnswers(shuffledAnswers);
  }, [
    quizQuestions,
    quizType,
    soundAnswerCharacter,
    allSoundAnswerOptions,
    extraSoundAnswersData,
    isSoundChoiceQuiz,
  ]);

  useEffect(() => {
    const getData = async () => {
      // This runs only on the first render
      try {
        console.log("quizCat:", quizCat, "quizType:", quizType);
        setPinyinInput("");
        const questionsResponse = await axiosPrivate.get(
          `/quizQuestions?user=${currentUser.id}&cat=${quizCat}&type=${quizType}`,
        );
        // console.log("179 questionsResponse", JSON.stringify(questionsResponse.data, null, 2));
        // If no more questions, navigate back
        if (questionsResponse.data.length === 0) {
          console.log("No more questions available. Navigating back...");
          setLoading(false);
          if (quizCat.startsWith("Duo")) {
            // Extract the section number (the single digit before ":")
            const sectionMatch = quizCat.match(/^Duo(\d+):/);
            const section = sectionMatch ? sectionMatch[1] : null;
            if (section) {
              navigate("/quiz/duolingo", { state: { section } });
              return;
            }
          }
          if (quizCat.startsWith("Dom")) {
            const courseMatch = quizCat.match(/^Dom(\d+):/);
            const course = courseMatch ? courseMatch[1] : null;
            if (course) {
              console.log("Navigating to Dom course:", course);
              navigate("/quiz/domino", { state: { course } });
              return;
            }
          }
          if (quizCat.startsWith("Ci")) {
            const courseMatch = quizCat.match(/^Ci(\d+):/);
            const course = courseMatch ? courseMatch[1] : null;
            if (course) {
              console.log("Navigating to Confucius course:", course);
              navigate("/quiz/confucius", { state: { course } });
              return;
            }
          }
          navigate(-1);
          return;
        }
        console.log(`59 New Hanzi ID: ${questionsResponse.data[0].hanzi_id}, Char: ${questionsResponse.data[0].hanzi_character},
          Priority_id: ${questionsResponse.data[0].priority_id}, Flag_flag: ${questionsResponse.data[0].flag_flag}, HSK: ${questionsResponse.data[0].hanzi_hsk_level}, HSK Comp: ${questionsResponse.data[0].hanzi_hsk_comp}
          `);

        // console.log("77 QuestionData: ", JSON.stringify(questionsResponse.data[0]));
        setQuizQuestions(questionsResponse.data);

        // Fetch extra sound answer options for when fewer than 17
        if (isSoundChoiceQuiz) {
          const extraSoundAnswersResponse = await axiosPrivate.get(`/questExtraSoundAns`);
          // console.log("extraSoundAnswersResponse.data", JSON.stringify(extraSoundAnswersResponse.data));
          setExtraSoundAnswersData(extraSoundAnswersResponse.data);
        }
        const partsPromises = Array.from({ length: 10 }, (_, i) => {
          const partId = questionsResponse.data[0][`hanzi_part${i}`];
          return partId ? axiosPrivate.get(`/questParts?hanzi=${partId}`) : Promise.resolve(null);
        });

        const partsResults = await Promise.all(partsPromises);
        const parts = partsResults.map((result) => result?.data?.[0]).filter(Boolean);
        setPartsData(parts);
        // Fetch subparts for all parts
        const subPartsPromises = parts.map((part, index) =>
          Promise.all(
            Array.from({ length: 10 }, (_, i) => {
              const subPartId = part[`hanzi_part${i}`];
              return subPartId
                ? axiosPrivate.get(`/questParts?hanzi=${subPartId}`)
                : Promise.resolve(null);
            }),
          ),
        );
        const subPartsResults = await Promise.all(subPartsPromises);
        const subParts = subPartsResults.map((results) =>
          results.map((result) => result?.data?.[0]).filter(Boolean),
        );
        // Update state with subparts data
        const subPartsDataMap = {};
        subParts.forEach((subPartArray, index) => {
          subPartsDataMap[index] = subPartArray;
        });
        setSubPartsData(subPartsDataMap);

        // Fetch uses Char
        if (questionsResponse.data[0].hanzi_type < 3) {
          const usesCharResponse = await axiosPrivate.get(
            `/questUsesChar?id=${questionsResponse.data[0]["hanzi_id"]}&type=${questionsResponse.data[0]["hanzi_type"]}`,
          );
          // console.log("type 1&2 usesCharesponse.data", JSON.stringify(usesCharResponse.data));
          setUsesCharData(usesCharResponse.data);
        } else {
          // For multi-character records, fetch uses char data for each character
          const characters = questionsResponse.data[0].hanzi_character.split("");
          console.log("usesChar characters: " + characters);
          const usesCharPromises = characters.map((char) =>
            axiosPrivate.get(`/questUsesChar?character=${char}`),
          );
          const usesCharResults = await Promise.all(usesCharPromises);
          const usesCharDataMap = {};
          usesCharResults.forEach((result, index) => {
            usesCharDataMap[characters[index]] = result.data;
          });
          console.log("type 3&4 usesCharesponse.data", JSON.stringify(usesCharDataMap.data));
          setUsesCharData(usesCharDataMap);
        }

        // Fetch uses MCW
        const usesMCWResponse = await axiosPrivate.get(
          `/questUsesMCW?character=${questionsResponse.data[0]["hanzi_character"]}&id=${questionsResponse.data[0]["hanzi_id"]}`,
        );
        // console.log("usesMCWResponse.data", JSON.stringify(usesMCWResponse.data));
        setUsesMCWData(usesMCWResponse.data);

        // Fetch phrases
        const phrasesResponse = await axiosPrivate.get(
          `/questPhrases?character=${questionsResponse.data[0]["hanzi_character"]}&id=${questionsResponse.data[0]["hanzi_id"]}`,
        );
        // console.log("phrasesResponse.data", JSON.stringify(phrasesResponse.data));
        setPhrasesData(phrasesResponse.data);

        // Fetch DYZ (多音字) when character has >1 pronunciations
        console.log("Hanzi DYZ query.  hanzi_dyz = " + questionsResponse.data[0].hanzi_dyz);
        if (questionsResponse.data[0].hanzi_dyz > 0) {
          const dyzResponse = await axiosPrivate.get(
            `/questDyz?id=${questionsResponse.data[0]["hanzi_id"]}&character=${questionsResponse.data[0]["hanzi_character"]}`,
          );
          console.log("dyzResponse.data", JSON.stringify(dyzResponse.data));
          setDyzData(dyzResponse.data);
        }

        if (
          shouldAutoPlayOnQuestionLoad &&
          !audioPlayed.current &&
          questionsResponse.data[0]?.hanzi_character
        ) {
          console.log("Audio 232: ", questionsResponse.data[0]?.hanzi_character);
          audioPlayed.current = true;
          if (quizCat !== "radMini") {
            handlePlayAudio(
              questionsResponse.data[0]?.hanzi_character,
              questionsResponse.data[0]?.hanzi_type,
            );
          } else {
            handlePlayAudio(
              questionsResponse.data[0]?.hanzi_char_full,
              questionsResponse.data[0]?.hanzi_type,
            );
          }
        }
        quizCat === "Hsk2measure" || quizCat === "Hsk3measure"
          ? setQuizMeaning(questionsResponse.data[0].hanzi_measure)
          : (quizType === "MeaPin" || quizType === "MeaHan") &&
              questionsResponse.data[0].hanzi_meaning_hint?.length > 0
            ? setQuizMeaning(`${questionsResponse.data[0].hanzi_meaning}`)
            : setQuizMeaning(questionsResponse.data[0].hanzi_meaning);
        setAllSoundAnswerOptions(questionsResponse.data);
        if (questionsResponse.data[0].priority_id > 0) {
          setPriorityId(questionsResponse.data[0].priority_id);
          setShowPriorityActions(false);
        }
        if (questionsResponse.data[0].flag_id > 0) {
          setFlagId(questionsResponse.data[0].flag_id);
          setFlagNote(questionsResponse.data[0].flag_note);
          setFlagFlag(questionsResponse.data[0].flag_flag);
        }
        // if (questionsResponse.data[0].discuss_id > 0) {
        //   setDiscussId(questionsResponse.data[0].discus_id);
        //   setDiscussNote(questionsResponse.data[0].discus_note);
        //   setDiscussNotify(questionsResponse.data[0].discus_notify);
        // }
        setHanziNote(questionsResponse.data[0].hanzi_note || "");
        setMeaningHint(questionsResponse.data[0].hanzi_meaning_hint || "");
        const scoreResponse = await axiosPrivate.get(
          `/quizScore?user=${currentUser.id}&cat=${quizCat}&type=${quizType}`,
        );
        // console.log("scoreResponse", scoreResponse);
        setQuizScore(scoreResponse.data[0]);
        // Fetch paronym data
        if (questionsResponse.data[0].hanzi_type < 3) {
          const ptext = questionsResponse.data[0].hanzi_ptext;
          const letters = ptext.replace(/\d+$/, ""); // Removes any trailing digit
          const id = questionsResponse.data[0].hanzi_id;
          // console.log("questParonyms letters:", letters);
          const paronymResponse = await axiosPrivate.get(
            `/questParonyms?letters=${letters}&id=${id}`,
          );
          // console.log("paronymResponse.data", JSON.stringify(paronymResponse.data));
          setParonymData(paronymResponse.data);
        } else {
          const ptext = questionsResponse.data[0].hanzi_ptext;
          const letters = ptext.replace(/\d+/g, "").replace(/\s+/g, "-"); // Remove numbers and replace spaces with hyphens
          const id = questionsResponse.data[0].hanzi_id;
          // console.log("questParonyms letters:", letters);
          const paronymResponse = await axiosPrivate.get(
            `/questParonyms?letters=${letters}&id=${id}`,
          );
          // console.log("paronymResponse.data", JSON.stringify(paronymResponse.data));
          setParonymData(paronymResponse.data);
        }
      } catch (error) {
        if (error.name === "CanceledError") {
          console.log("Request was canceled.");
          return;
        }
        if (error.response?.status === 401 || error.response?.status === 403) {
          navigate("/login", { state: { from: location.pathname }, replace: true });
        } else {
          console.error("Error retrieving data:", error);
          navigate("/login", { state: { from: location.pathname }, replace: true });
        }
      } finally {
        setLoading(false);
        if (!isSoundChoiceQuiz && clickMode === 0) {
          setTimeout(() => {
            pinyinTextFieldRef.current.focus();
          }, 0); // Delay to ensure DOM is updated
        }
      }
    };
    getData();
    // WARNING!!  I do NOT want this to rerun each time the user answers a question, so be cautious with dependencies
    // The line below prevents the linter from complaining about the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosPrivate, navigate, location.pathname, currentUser.id, quizType, quizCat, clickMode]);

  useEffect(() => {
    // Trigger handleNewSoundQuestion when quizQuestions change
    if (isSoundChoiceQuiz && allSoundAnswerOptions.length > 0) {
      console.log(
        "useEffect Triggering handleNewSoundQuestion for ",
        quizQuestions[0]?.hanzi_character,
      );
      handleNewSoundQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizQuestions, quizType, handleNewSoundQuestion]);

  const handleSoundAnswer = (answer, type) => {
    console.log("handleSoundAnswer answer:", answer + " type:", type + " score:", soundScore);
    handlePlayAudio(answer, type);
    if (soundAnswerCharacter === answer) {
      // Correct answer logic
      console.log("Correct answer selected!", answer);
      setSelectedCorrectSoundAnswer(answer);
      if (quizCat !== "radMini") {
        handleFlip(quizQuestions[0]?.hanzi_character, quizQuestions[0]?.hanzi_type);
      } else {
        handleFlip(quizQuestions[0]?.hanzi_char_full, quizQuestions[0]?.hanzi_type);
      }
      return;
    }
    // Incorrect answer logic
    console.log("Incorrect answer selected!", answer);
    setIncorrectSoundAnswers((prev) => [...prev, answer]);
    setIncorrectSoundAttempts((prev) => {
      const newAttempts = prev + 1;
      const newSoundScore = 6 - newAttempts * 2; // 6, 4, or 2 based on attempts
      console.log("Updated incorrectSoundAttempts:", newAttempts);
      console.log("New soundScore:", newSoundScore);
      if (!flippedOnce) {
        setSoundScore(newSoundScore);
      }
      return newAttempts;
    });
    if (incorrectSoundAttempts === 2) {
      if (quizCat !== "radMini") {
        handleFlip(quizQuestions[0]?.hanzi_character, quizQuestions[0]?.hanzi_type);
      } else {
        handleFlip(quizQuestions[0]?.hanzi_char_full, quizQuestions[0]?.hanzi_type);
      }
    }
  };

  useEffect(() => {
    if (selectedCorrectSoundAnswer) {
      console.log("Selected correct answer updated to:", selectedCorrectSoundAnswer);
    }
  }, [selectedCorrectSoundAnswer]);

  useEffect(() => {
    flippedRef.current = flipped;
  }, [flipped]);

  const handleFlip = (text, type, options = {}) => {
    const { forceToggle = false } = options;
    console.log("handleFlip");
    if (!flippedOnce || forceToggle) {
      setShowHint(false);
      const nextFlipped = !flippedRef.current;
      setFlipped(nextFlipped);
      if (!flippedOnce) {
        currentUser.timer > 0 && restartTimer(); // Restart timer if it's enabled
      }
      if (nextFlipped && !isSoundChoiceQuiz) {
        setTimeout(() => {
          // console.log("Audio 357: ", quizQuestions[0]?.hanzi_character);
          handlePlayAudio(text, type);
        }, 500); // Delay in milliseconds
        // } else {
        //   if (!selectedCorrectSoundAnswer) {
        //     console.log("selectedCorrectSoundAnswer: " + selectedCorrectSoundAnswer + "; Setting soundScore to 0");
        //     setSoundScore(0); // No points if you flip before selecting the correct answer
        //   }
      }
      if (!flippedOnce) {
        setFlippedOnce(true); // Mark as flipped once
      }
      if (!isSoundChoiceQuiz && (clickMode === false || clickMode === 0)) {
        setTimeout(() => {
          pinyinTextFieldRef.current.focus();
        }, 0); // Delay to ensure DOM is updated
      } else if (clickMode === 1 || clickMode === true) {
        console.log("400 handleFlip clickMode:", clickMode);
        setEnableAnswerBoxes(true);
      }
    }
  };

  const handlePlayAudio = async (text, type) => {
    if (!text) return alert("Unable to play audio because no character found.");
    const spokenCharacter = type === 1 ? text.charAt(0) : text;
    setAudioPlaying(true);
    // console.log("Polly Text:", spokenCharacter);
    try {
      const response = await axiosPrivate.post(
        "/polly",
        { text: spokenCharacter },
        { responseType: "blob" },
      );
      // console.log("Polly response: " + response);
      const audioUrl = URL.createObjectURL(new Blob([response.data], { type: "audio/mpeg" }));
      // console.log("audioURL", audioUrl);
      const audio = new Audio(audioUrl);
      audio.onended = () => setAudioPlaying(false);
      await audio.play();
      setTimeout(() => {
        pinyinTextFieldRef.current?.focus();
      }, 0);
    } catch (error) {
      console.error("Error generating audio:", error);
      alert("Failed to generate audio.");
      setAudioPlaying(false);
    }
  };

  const handlePinyinInput = (event) => {
    let inputValue = event.target.value;
    const normalizeLettersAndNumbers = (str) => str.replace(/\s+/g, "").trim().toLowerCase();
    const normalizeLettersOnly = (str) =>
      str
        .replace(/[0-9\s]+/g, "")
        .trim()
        .toLowerCase();
    const normalizedInput = normalizeLettersAndNumbers(inputValue);
    const normalizedAnswer = normalizeLettersAndNumbers(quizQuestions[0]?.hanzi_ptext);
    const normalizedLettersInput = normalizeLettersOnly(inputValue);
    const normalizedLettersAnswer = normalizeLettersOnly(quizQuestions[0]?.hanzi_ptext);
    if (inputValue.includes("0")) {
      // console.log("0 detected in inputValue, enabling answer boxes...");
      setTimeout(() => {
        setEnableAnswerBoxes(true);
      }, 0); // Delay to ensure DOM is updated
      if (quizCat !== "radMini") {
        handleFlip(quizQuestions[0]?.hanzi_character, quizQuestions[0]?.hanzi_type, {
          forceToggle: true,
        });
      } else {
        handleFlip(quizQuestions[0]?.hanzi_char_full, quizQuestions[0]?.hanzi_type, {
          forceToggle: true,
        });
      }
      inputValue = inputValue.replace(/0/g, ""); // Remove all 0s from the input
    }
    if (inputValue.includes("9")) {
      if (quizCat !== "radMini") {
        handlePlayAudio(quizQuestions[0]?.hanzi_character, quizQuestions[0]?.hanzi_type);
      } else {
        handlePlayAudio(quizQuestions[0]?.hanzi_char_full, quizQuestions[0]?.hanzi_type);
      }
      inputValue = inputValue.replace(/9/g, ""); // Remove all 9s from the input
    }
    if (inputValue.includes("!")) {
      if (enableAnswerBoxes) {
        handleAnswer(1);
      }
      inputValue = "";
    } else if (inputValue.includes("@")) {
      if (enableAnswerBoxes) {
        handleAnswer(2);
      }
      inputValue = "";
    } else if (inputValue.includes("#")) {
      if (enableAnswerBoxes) {
        handleAnswer(3);
      }
      inputValue = "";
    } else if (inputValue.includes("$")) {
      if (enableAnswerBoxes) {
        handleAnswer(4);
      }
      inputValue = "";
    } else if (inputValue.includes("%")) {
      if (enableAnswerBoxes) {
        handleAnswer(5);
      }
      inputValue = "";
    } else if (inputValue.includes("^")) {
      if (enableAnswerBoxes) {
        handleAnswer(6);
      }
      inputValue = "";
    }
    if (inputValue.includes("7")) {
      setCommenting(true);
      setEnableAnswerBoxes(false);
      setTimeout(() => {
        hanziNoteRef.current?.focus();
      }, 0);
      inputValue = inputValue.replace(/7/g, "");
    }
    if (inputValue.includes("8")) {
      if (quizQuestions[0]?.hanzi_meaning_hint && quizQuestions[0]?.hanzi_meaning_hint.length > 0) {
        setShowHint(true);
      }
      inputValue = inputValue.replace(/8/g, "");
    }
    if (normalizedInput === normalizedAnswer) {
      if (quizCat !== "radMini") {
        handleFlip(quizQuestions[0]?.hanzi_character, quizQuestions[0]?.hanzi_type);
      } else {
        handleFlip(quizQuestions[0]?.hanzi_char_full, quizQuestions[0]?.hanzi_type);
      }
      setPinyinMatch(true);
      setPinyinLetterMatch(false);
      setEnableAnswerBoxes(true);
    } else if (normalizedLettersInput === normalizedLettersAnswer) {
      setPinyinMatch(false);
      setPinyinLetterMatch(true);
      setEnableAnswerBoxes(false);
    } else {
      setPinyinMatch(false);
      setPinyinLetterMatch(false);
      setEnableAnswerBoxes(false);
    }
    setPinyinInput(inputValue);
  };

  // useEffect(() => {
  //   console.log("quizQuestions:", quizQuestions[0]);
  // }, [quizQuestions]);

  const handleFlagSave = async (action) => {
    if (!quizQuestions) return;
    const flagParams = {
      action: action,
      flagId: flagId,
      hanzi: quizQuestions[0]?.hanzi_id,
      user: currentUser.id,
      note: action === "add" || action === "update" ? flagNote : null,
    };
    try {
      const response = await axiosPrivate.post(`/questionFlag`, flagParams);
      console.log("Flag submitted:", flagParams, "Response:", response.data);
      if (response.data.flagId !== undefined) {
        setFlagId(response.data.flagId);
      }
      setFlagging(false); // Exit flagging mode
    } catch (error) {
      console.error("Error modifying flag:", error);
    } finally {
      if (!isSoundChoiceQuiz && clickMode === 0) {
        setTimeout(() => {
          pinyinTextFieldRef.current.focus();
        }, 0); // Delay to ensure DOM is updated
      }
      if (flagNote === "") {
        setFlagFlag(0);
      } else {
        setFlagFlag(1);
      }
      setTimeout(() => {
        setEnableAnswerBoxes(true);
      }, 0);
    }
  };

  const handleFlagIconOpen = () => {
    // setDiscussing(false); // Exit discussing mode
    setFlagging(true);
    setEnableAnswerBoxes(false); // Disable answer buttons
    // Make the flag text field focus after the DOM updates
    setTimeout(() => {
      flagTextFieldRef.current?.focus();
    }, 0); // Use a small timeout (0ms) to ensure the DOM updates
  };

  const handleFlagIconFlag = async () => {
    const newFlagFlag = flagFlag === 1 ? 0 : 1;
    try {
      await axiosPrivate.get(`/questionFlagToggle?flagId=${flagId}&flagFlag=${newFlagFlag}`);
    } catch (error) {
      console.error("Error toggling flag:", error);
    }
    setFlagFlag(newFlagFlag);
  };

  // const handleDiscussSave = async (action) => {
  //   if (!quizQuestions) return;
  //   const discussParams = {
  //     action: action,
  //     discussId: discussId,
  //     hanzi: quizQuestions[0]?.hanzi_id,
  //     user: currentUser.id,
  //     note: action === "add" || action === "update" ? discussNote : null,
  //   };
  //   try {
  //     const response = await axiosPrivate.post(`/questionDiscuss`, discussParams);
  //     console.log("Discussion submitted:", discussParams, "Response:", response.data);
  //     if (response.data.discussId !== undefined) {
  //       setDiscussId(response.data.discussId);
  //     }
  //     setDiscussing(false); // Exit discussing mode
  //   } catch (error) {
  //     console.error("Error modifying discuss:", error);
  //   } finally {
  //     if (quizType !== "PinHan" && quizType !== "MeaHan" && clickMode === 0) {
  //       setTimeout(() => {
  //         pinyinTextFieldRef.current.focus();
  //       }, 0); // Delay to ensure DOM is updated
  //     }
  //     if (discussNote === "") {
  //       setDiscussNotify(0);
  //     } else {
  //       setDiscussNotify(1);
  //     }
  //     setTimeout(() => {
  //       setEnableAnswerBoxes(true);
  //     }, 0);
  //   }
  // };

  const handleNoteSave = async () => {
    if (!quizQuestions) return;
    try {
      await axiosPrivate.post(`/questionNote`, {
        hanziId: quizQuestions[0]?.hanzi_id,
        note: hanziNote,
      });
      console.log("Note saved successfully");
      setCommenting(false);
      setEnableAnswerBoxes(true);
      if (!isSoundChoiceQuiz && clickMode === 0) {
        setTimeout(() => {
          pinyinTextFieldRef.current.focus();
        }, 0);
      }
    } catch (error) {
      console.error("Error saving note:", error);
    }
  };

  const handleHintSave = async () => {
    if (!quizQuestions) return;
    try {
      await axiosPrivate.post(`/questionHint`, {
        hanziId: quizQuestions[0]?.hanzi_id,
        hint: meaningHint,
      });
      console.log("Hint saved successfully");
      setCommenting(false);
      setEnableAnswerBoxes(true);
      if (!isSoundChoiceQuiz && clickMode === 0) {
        setTimeout(() => {
          pinyinTextFieldRef.current.focus();
        }, 0);
      }
    } catch (error) {
      console.error("Error saving hint:", error);
    }
  };

  // const handleDiscussIconOpen = () => {
  //   setFlagging(false); // Exit flagging mode
  //   setDiscussing(true);
  //   setEnableAnswerBoxes(false); // Disable answer buttons
  //   // Make the discuss text field focus after the DOM updates
  //   setTimeout(() => {
  //     discussTextFieldRef.current?.focus();
  //   }, 0); // Use a small timeout (0ms) to ensure the DOM updates
  // };

  const handleCommentIconOpen = () => {
    setFlagging(false);
    if (quizQuestions[0]?.hanzi_meaning_hint && quizQuestions[0]?.hanzi_meaning_hint.length > 0) {
      setShowHint(true);
    } else {
      setCommenting(true);
      setEnableAnswerBoxes(false);
      setTimeout(() => {
        hanziNoteRef.current?.focus();
      }, 0);
    }
  };

  // const handleDiscussIconNotify = async () => {
  //   const newDiscussNotify = discussNotify === 1 ? 0 : 1;
  //   try {
  //     await axiosPrivate.get(`/questionDiscussToggle?discussId=${discussId}&discussNotify=${newDiscussNotify}`);
  //   } catch (error) {
  //     console.error("Error toggling discuss notify:", error);
  //   }
  //   setDiscussNotify(newDiscussNotify);
  // };

  const getCommentButtonColor = () => {
    const hasNote = hanziNote && hanziNote.length > 0;
    const hasHint =
      quizQuestions[0]?.hanzi_meaning_hint && quizQuestions[0]?.hanzi_meaning_hint.length > 0;

    if (hasHint) return "#000000"; // Black - hint has content
    if (hasNote && hasHint) return "#9c27b0"; // Purple - both fields have content
    if (hasNote) return "#2196f3"; // Blue - only note has content
    return "#757575"; // Grey - no content
  };

  const handlePriority = async () => {
    if (!quizQuestions) return;
    let priorityParams;
    try {
      if (priorityId > 0) {
        // Delete entry from the priority table
        priorityParams = `?action=delete&priorityId=${priorityId}`;
        const response = await axiosPrivate.get(`/questionPriority${priorityParams}`);
        console.log("Priority deleted:", response.data);
        setPriorityId(0);
        setShowPriorityActions(false);
      } else {
        // Add entry to the priority table
        priorityParams = `?action=add&hanzi=${quizQuestions[0]?.hanzi_id}&user=${currentUser.id}`;
        const response = await axiosPrivate.get(`/questionPriority${priorityParams}`);
        console.log("Priority added:", response.data);
        setPriorityId(response.data.priorityId);
        setShowPriorityActions(false);
      }
    } catch (error) {
      console.error("Error modifying priority:", error);
    } finally {
      if (!isSoundChoiceQuiz && clickMode === 0) {
        setTimeout(() => {
          pinyinTextFieldRef.current.focus();
        }, 0); // Delay to ensure DOM is updated
      }
    }
  };

  const handleSnoozePriority = async (snooze) => {
    if (!priorityId) return;
    try {
      await axiosPrivate.get(`/userPrioritySnooze?priorityId=${priorityId}&snooze=${snooze}`);
      setPriorityId(0);
      setShowPriorityActions(false);
    } catch (error) {
      console.error("Error snoozing priority:", error);
    } finally {
      if (!isSoundChoiceQuiz && clickMode === 0) {
        setTimeout(() => {
          pinyinTextFieldRef.current?.focus();
        }, 0);
      }
    }
  };

  const handleAnswer = async (score) => {
    if (!quizQuestions) return;
    console.log("Score: ", score);
    setCardLoading(true);
    setFlipped(false);
    setFlippedOnce(false);
    setPinyinInput("");
    setDisableAnimation(true);
    setEnableAnswerBoxes(false);
    setPartsData([]);
    setSubPartsData([]);
    setUsesCharData([]);
    setUsesMCWData([]);
    setPhrasesData([]);
    setDyzData([]);
    setParonymData([]);
    setShuffledSoundAnswers([]);
    setSelectedCorrectSoundAnswer(null);
    setIncorrectSoundAnswers([]);
    setIncorrectSoundAttempts(0);
    audioPlayed.current = false;
    const answerPayload = {
      hanziId: quizQuestions[0]?.hanzi_id,
      userId: currentUser.id,
      quizCat,
      quizType,
      score,
    };
    try {
      await axiosPrivate.post("/quizAnswers", answerPayload);
      console.log("Answer submitted:", answerPayload);
      const questionsResponse = await axiosPrivate.get(
        `/quizQuestions?user=${currentUser.id}&cat=${quizCat}&type=${quizType}`,
      );
      // If no more questions, navigate back
      // console.log("Remaining: ", questionsResponse.data.length);
      if (questionsResponse.data.length === 0) {
        console.log("No more questions available. Navigating back...");
        try {
          await axiosPrivate.get(
            `/quizExit?user=${currentUser.id}&cat=${quizCat}&type=${quizType}&remaining=${questionsResponse.data.length}`,
          );
        } catch (error) {
          console.error("Failed to log quiz exit:", error);
          // Optionally continue or halt navigation based on error severity
        }
        if (quizCat.startsWith("Duo")) {
          // Extract the section number (the single digit before ":")
          const sectionMatch = quizCat.match(/^Duo(\d+):/);
          const section = sectionMatch ? sectionMatch[1] : null;
          if (section) {
            navigate("/quiz/duolingo", { state: { section } });
            return;
          }
        }
        if (quizCat.startsWith("Dom")) {
          const courseMatch = quizCat.match(/^Dom(\d+):/);
          const course = courseMatch ? courseMatch[1] : null;
          if (course) {
            console.log("Navigating to Dom course:", course);
            navigate("/quiz/domino", { state: { course } });
            return;
          }
        }
        if (quizCat.startsWith("Ci")) {
          const courseMatch = quizCat.match(/^Ci(\d+):/);
          const course = courseMatch ? courseMatch[1] : null;
          if (course) {
            console.log("Navigating to Confucius course:", course);
            navigate("/quiz/confucius", { state: { course } });
            return;
          }
        }
        navigate(-1); // Navigate to the previous page
        return;
      }
      console.log(`678 New Hanzi ID: ${questionsResponse.data[0].hanzi_id}, Char: ${questionsResponse.data[0].hanzi_character},
        Priority_id: ${questionsResponse.data[0].priority_id}, Flag_flag: ${questionsResponse.data[0].flag_flag}, 
        Flag_note: ${questionsResponse.data[0].flag_note}, Discuss_id: ${questionsResponse.data[0].discuss_id},`);
      // console.log("SimSound: " + questionsResponse.data[0].hanzi_speech_part3);
      // console.log("78 QuestionData: ", JSON.stringify(questionsResponse.data[0]));
      setQuizQuestions(questionsResponse.data);
      const partsPromises = Array.from({ length: 10 }, (_, i) => {
        const partId = questionsResponse.data[0][`hanzi_part${i}`];
        return partId ? axiosPrivate.get(`/questParts?hanzi=${partId}`) : Promise.resolve(null);
      });

      const partsResults = await Promise.all(partsPromises);
      const parts = partsResults.map((result) => result?.data?.[0]).filter(Boolean);
      setPartsData(parts);
      // Fetch subparts for all parts
      const subPartsPromises = parts.map((part, index) =>
        Promise.all(
          Array.from({ length: 10 }, (_, i) => {
            const subPartId = part[`hanzi_part${i}`];
            return subPartId
              ? axiosPrivate.get(`/questParts?hanzi=${subPartId}`)
              : Promise.resolve(null);
          }),
        ),
      );
      const subPartsResults = await Promise.all(subPartsPromises);
      const subParts = subPartsResults.map((results) =>
        results.map((result) => result?.data?.[0]).filter(Boolean),
      );
      // Update state with subparts data
      const subPartsDataMap = {};
      subParts.forEach((subPartArray, index) => {
        subPartsDataMap[index] = subPartArray;
      });
      setSubPartsData(subPartsDataMap);

      // Fetch uses Char
      if (questionsResponse.data[0].hanzi_type < 3) {
        const usesCharResponse = await axiosPrivate.get(
          `/questUsesChar?id=${questionsResponse.data[0]["hanzi_id"]}&type=${questionsResponse.data[0]["hanzi_type"]}`,
        );
        // console.log("type 1&2 usesCharesponse.data", JSON.stringify(usesCharResponse.data));
        setUsesCharData(usesCharResponse.data);
      } else {
        // For multi-character records, fetch uses char data for each character
        const characters =
          questionsResponse.data[0].hanzi_type === 1
            ? [questionsResponse.data[0].hanzi_character.split(" ")[0].charAt(0)] // Type 1: use first character of first word
            : questionsResponse.data[0].hanzi_character.split(""); // Type 3,4: use all characters
        const usesCharPromises = characters.map((char) =>
          axiosPrivate.get(
            `/questUsesChar?id=${questionsResponse.data[0]["hanzi_id"]}&character=${char}`,
          ),
        );
        const usesCharResults = await Promise.all(usesCharPromises);
        const usesCharDataMap = {};
        usesCharResults.forEach((result, index) => {
          usesCharDataMap[characters[index]] = result.data;
        });
        console.log("type 3&4 usesCharesponse.data", JSON.stringify(usesCharDataMap.data));
        setUsesCharData(usesCharDataMap);
      }

      // Fetch uses MCW
      const usesMCWResponse = await axiosPrivate.get(
        `/questUsesMCW?character=${questionsResponse.data[0]["hanzi_character"]}&id=${questionsResponse.data[0]["hanzi_id"]}`,
      );
      // console.log("usesResponse.data", JSON.stringify(usesResponse.data));
      setUsesMCWData(usesMCWResponse.data);

      // Fetch phrases
      const phrasesResponse = await axiosPrivate.get(
        `/questPhrases?character=${questionsResponse.data[0]["hanzi_character"]}&id=${questionsResponse.data[0]["hanzi_id"]}`,
      );
      // console.log("phrasesResponse.data", JSON.stringify(phrasesResponse.data));
      setPhrasesData(phrasesResponse.data);

      // Fetch DYZ (多音字) when character has >1 pronunciations
      console.log("Hanzi DYZ query2.  hanzi_dyz = " + questionsResponse.data[0].hanzi_dyz);
      if (questionsResponse.data[0].hanzi_dyz > 0) {
        const dyzResponse = await axiosPrivate.get(
          `/questDyz?id=${questionsResponse.data[0]["hanzi_id"]}&character=${questionsResponse.data[0]["hanzi_character"]}`,
        );
        // console.log("dyzResponse.data", JSON.stringify(phrasesResponse.data));
        setDyzData(dyzResponse.data);
      }
      // Fetch paronym data
      if (questionsResponse.data[0].hanzi_type < 3) {
        const ptext = questionsResponse.data[0].hanzi_ptext;
        const letters = ptext.replace(/\d+$/, ""); // Removes any trailing digit
        const id = questionsResponse.data[0].hanzi_id;
        // console.log("questParonyms letters:", letters);
        const paronymResponse = await axiosPrivate.get(
          `/questParonyms?letters=${letters}&id=${id}`,
        );
        // console.log("paronymResponse.data", JSON.stringify(paronymResponse.data));
        setParonymData(paronymResponse.data);
      } else {
        const ptext = questionsResponse.data[0].hanzi_ptext;
        const letters = ptext.replace(/\d+/g, "").replace(/\s+/g, "-"); // Remove numbers and replace spaces with hyphens
        const id = questionsResponse.data[0].hanzi_id;
        // console.log("questParonyms letters:", letters);
        const paronymResponse = await axiosPrivate.get(
          `/questParonyms?letters=${letters}&id=${id}`,
        );
        // console.log("paronymResponse.data", JSON.stringify(paronymResponse.data));
        setParonymData(paronymResponse.data);
      }

      quizCat === "Hsk2measure" || quizCat === "Hsk3measure"
        ? setQuizMeaning(questionsResponse.data[0].hanzi_measure)
        : (quizType === "MeaPin" || quizType === "MeaHan") &&
            questionsResponse.data[0].hanzi_meaning_hint?.length > 0
          ? setQuizMeaning(`${questionsResponse.data[0].hanzi_meaning}`)
          : setQuizMeaning(questionsResponse.data[0].hanzi_meaning);
      if (questionsResponse.data[0].priority_id > 0) {
        setPriorityId(questionsResponse.data[0].priority_id);
        setShowPriorityActions(false);
      } else {
        setPriorityId(0);
        setShowPriorityActions(false);
      }
      if (questionsResponse.data[0].flag_id > 0) {
        setFlagId(questionsResponse.data[0].flag_id);
        setFlagNote(questionsResponse.data[0].flag_note);
        setFlagFlag(questionsResponse.data[0].flag_flag);
      } else {
        setFlagId(0);
        setFlagNote("");
        setFlagFlag(0);
      }
      // if (questionsResponse.data[0].discuss_id > 0) {
      //   setDiscussId(questionsResponse.data[0].discuss_id);
      //   setDiscussNote(questionsResponse.data[0].discuss_note);
      //   setDiscussNotify(questionsResponse.data[0].discuss_notify);
      // } else {
      //   setDiscussId(0);
      //   setDiscussNote("");
      //   setDiscussNotify(0);
      // }
      setHanziNote(questionsResponse.data[0].hanzi_note || "");
      setMeaningHint(questionsResponse.data[0].hanzi_meaning_hint || "");
      setSoundScore(6);
      setFlagging(false);
      // setDiscussing(false);
      setCommenting(false);
      setShowHint(false);
      // Trigger a re-render
      setRenderKey((prevKey) => prevKey + 1);
      setPinyinLetterMatch(false);
      setPinyinMatch(false);
      const scoreResponse = await axiosPrivate.get(
        `/quizScore?user=${currentUser.id}&cat=${quizCat}&type=${quizType}`,
      );
      console.log("Score: ", scoreResponse.data[0]);
      setQuizScore(scoreResponse.data[0]);
      setTimeout(() => setDisableAnimation(false), 500);
      setRemainingTime(currentUser.timer); // Reset timer to the original value
      //       currentUser.timer > 0 && restartTimer(); // Restart timer if it's enabled >> commented out on 2/27/25
      // console.log("Quiz questions refreshed:", questionsResponse.data);
      if (shouldAutoPlayOnQuestionLoad && questionsResponse.data[0]?.hanzi_character) {
        console.log("Audio 785: ", questionsResponse.data[0]?.hanzi_character);
        audioPlayed.current = true;
        if (quizCat !== "radMini") {
          handlePlayAudio(
            questionsResponse.data[0]?.hanzi_character,
            questionsResponse.data[0]?.hanzi_type,
          );
        } else {
          handlePlayAudio(
            questionsResponse.data[0]?.hanzi_char_full,
            questionsResponse.data[0]?.hanzi_type,
          );
        }
      }
    } catch (error) {
      console.error("Error submitting answer:", error);
    } finally {
      if (!isSoundChoiceQuiz && clickMode === 0) {
        setTimeout(() => {
          pinyinTextFieldRef.current.focus();
        }, 0); // Delay to ensure DOM is updated
      }
      setCardLoading(false);
    }
  };

  const renderParts = (data, showBUpp = true) => {
    // console.log("renderRow data:", data);
    if (!data) return null;
    const { hanzi_character, hanzi_pinyin, hanzi_meaning, hanzi_type } = data;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <Typography variant="body1" sx={{ fontSize: "22px", fontWeight: 300, lineHeight: 1.1 }}>
          {hanzi_character.replace(/\s+/g, "")}
        </Typography>
        <Typography
          variant="body1"
          onClick={audioPlaying ? null : () => handlePlayAudio(hanzi_character, hanzi_type)}
          sx={{ color: "primary.main", fontFamily: "Spectral", cursor: "pointer", lineHeight: 1.2 }}
        >
          {hanzi_pinyin}
        </Typography>
        <Typography variant="body1" sx={{ fontSize: "14px", lineHeight: 1.2 }}>
          {hanzi_meaning}
        </Typography>
        <RenderBadges
          data={data}
          axiosPrivate={axiosPrivate}
          setSimSoundData={setSimSoundData}
          setSimSoundModalOpen={setSimSoundModalOpen}
          showBUpp={showBUpp}
          handleOpenModalUpp={handleOpenModalUpp}
        />
      </Box>
    );
  };

  const renderSubParts = (data, showBUpp = true) => {
    // console.log("renderRow data:", data);
    if (!data) return null;
    const { hanzi_character, hanzi_pinyin, hanzi_meaning, hanzi_type } = data;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", ml: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography variant="body1" sx={{ fontSize: "22px", fontWeight: 300, lineHeight: 1.2 }}>
            {hanzi_character.replace(/\s+/g, "")}
          </Typography>
          <Typography
            variant="body1"
            onClick={audioPlaying ? null : () => handlePlayAudio(hanzi_character, hanzi_type)}
            sx={{
              color: "primary.main",
              fontFamily: "Spectral",
              cursor: "pointer",
              lineHeight: 1.2,
            }}
          >
            {hanzi_pinyin}
          </Typography>
          <RenderBadges
            data={data}
            axiosPrivate={axiosPrivate}
            setSimSoundData={setSimSoundData}
            setSimSoundModalOpen={setSimSoundModalOpen}
            showBUpp={showBUpp}
            handleOpenModalUpp={handleOpenModalUpp}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Typography variant="body1" sx={{ fontSize: "14px", lineHeight: 1.2 }}>
            {hanzi_meaning}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderPhrases = (data, showBUpp = true) => {
    // console.log("renderRow data:", data);
    if (!data) return null;
    const { hanzi_character, hanzi_pinyin, hanzi_meaning, hanzi_type } = data;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Typography variant="body1" sx={{ fontSize: "22px", fontWeight: 300, lineHeight: 1.1 }}>
            {hanzi_character.replace(/\s+/g, "")}
          </Typography>
          <Typography
            variant="body1"
            onClick={audioPlaying ? null : () => handlePlayAudio(hanzi_character, hanzi_type)}
            sx={{
              color: "primary.main",
              fontFamily: "Spectral",
              cursor: "pointer",
              lineHeight: 1.2,
            }}
          >
            {hanzi_pinyin}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Typography variant="body1" sx={{ fontSize: "14px", lineHeight: 1.2 }}>
            {hanzi_meaning}
          </Typography>
          <RenderBadges
            data={data}
            axiosPrivate={axiosPrivate}
            setSimSoundData={setSimSoundData}
            setSimSoundModalOpen={setSimSoundModalOpen}
            showBUpp={showBUpp}
            handleOpenModalUpp={handleOpenModalUpp}
          />
        </Box>
      </Box>
    );
  };

  const handleOpenModalUpp = async (
    hanziId,
    type,
    character,
    pinyin,
    ptext,
    measure,
    dyz,
    hskLevel,
    hskComp,
    freq,
    speech,
    proper,
    duoSection,
    duoUnit,
    domChapter,
    domCourse,
    domLesson,
    speechPart3,
    uncommon,
  ) => {
    const modalData = {
      hanziId,
      type,
      character,
      pinyin,
      ptext,
      measure,
      dyz,
      hskLevel,
      hskComp,
      freq,
      speech,
      proper,
      duoSection,
      duoUnit,
      domChapter,
      domCourse,
      domLesson,
      speechPart3,
      uncommon,
    };
    setModalUppData(modalData);
    try {
      // First get the hanzi record to access its part fields
      const hanziResponse = await axiosPrivate.get(`/questParts?hanzi=${hanziId}`);
      const hanziRecord = hanziResponse.data?.[0];

      if (hanziRecord) {
        const partsPromises = Array.from({ length: 10 }, (_, i) => {
          const partId = hanziRecord[`hanzi_part${i}`];
          return partId ? axiosPrivate.get(`/questParts?hanzi=${partId}`) : Promise.resolve(null);
        });
        const partsResults = await Promise.all(partsPromises);
        const parts = partsResults.map((result) => result?.data?.[0]).filter(Boolean);
        setModalPartsData(parts);

        // Fetch subparts for modal
        const subPartsPromises = parts.map((part, index) =>
          Promise.all(
            Array.from({ length: 10 }, (_, i) => {
              const subPartId = part[`hanzi_part${i}`];
              return subPartId
                ? axiosPrivate.get(`/questParts?hanzi=${subPartId}`)
                : Promise.resolve(null);
            }),
          ),
        );
        const subPartsResults = await Promise.all(subPartsPromises);
        const subParts = subPartsResults.map((results) =>
          results.map((result) => result?.data?.[0]).filter(Boolean),
        );
        const subPartsDataMap = {};
        subParts.forEach((subPartArray, index) => {
          subPartsDataMap[index] = subPartArray;
        });
        setModalSubPartsData(subPartsDataMap);
      } else {
        setModalPartsData([]);
        setModalSubPartsData([]);
      }

      if (modalData.dyz > 0) {
        const dyzResponse = await axiosPrivate.get(
          `/questDyz?id=${hanziId}&character=${character}`,
        );
        setModalDyzData(dyzResponse.data);
      }

      if (type < 3) {
        const usesCharResponse = await axiosPrivate.get(
          `/questUsesChar?id=${hanziId}&type=${type}`,
        );
        setModalUsesCharData(usesCharResponse.data);
      } else if (character) {
        const characters = type === 1 ? [character.split(" ")[0].charAt(0)] : character.split("");
        const usesCharPromises = characters.map((char) =>
          axiosPrivate.get(`/questUsesChar?id=${hanziId}&character=${char}`),
        );
        const usesCharResults = await Promise.all(usesCharPromises);
        const usesCharDataMap = {};
        usesCharResults.forEach((result, index) => {
          usesCharDataMap[characters[index]] = result.data;
        });
        setModalUsesCharData(usesCharDataMap);
      }

      const usesMCWResponse = await axiosPrivate.get(
        `/questUsesMCW?character=${character}&id=${hanziId}`,
      );
      setModalUsesMCWData(usesMCWResponse.data);
      const phrasesResponse = await axiosPrivate.get(
        `/questPhrases?character=${character}&id=${hanziId}`,
      );
      setModalPhrasesData(phrasesResponse.data);

      if (type < 3) {
        const letters = ptext.replace(/\d+$/, "");
        const paronymResponse = await axiosPrivate.get(
          `/questParonyms?letters=${letters}&id=${hanziId}`,
        );
        setModalParonymData(paronymResponse.data);
      } else {
        const letters = ptext.replace(/\d+/g, "").replace(/\s+/g, "-");
        const paronymResponse = await axiosPrivate.get(
          `/questParonyms?letters=${letters}&id=${hanziId}`,
        );
        setModalParonymData(paronymResponse.data);
      }
      setModalUppOpen(true);
    } catch (error) {
      console.error("Error retrieving use, phrase, and paronym data:", error);
    }
  };

  const handleCloseModalUpp = () => {
    setModalUppOpen(false);
    setModalUppData(null);
    setModalDyzData([]);
    setModalPartsData([]);
    setModalSubPartsData([]);
    setModalUsesCharData([]);
    setModalUsesMCWData([]);
    setModalPhrasesData([]);
    setModalParonymData([]);
  };

  if (loading) {
    return (
      <Typography variant="body1" sx={{ textAlign: "center", mt: 5 }}>
        Loading...
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: "background.default",
        color: "text.primary",
        mx: 0,
        mt: 2,
        mb: 8,
        padding: 0,
        pb: "100px",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          width: "98%",
          // maxWidth: quizCat.startsWith("Phrase") && windowWidth > 600 ? "600px" : "400px",
          maxWidth:
            quizQuestions[0].hanzi_character.length > 6 && windowWidth > 600 ? "600px" : "400px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          bgcolor: "background.default",
          color: "text.primary",
          margin: "auto",
          px: 2,
        }}
      >
        {/* Header with Section Info */}
        <Box
          sx={{
            display: "flex",
            position: "relative",
            width: "100%",
            alignItems: "center",
            mb: 2,
            mt: 2,
          }}
        >
          <Typography variant="body1" sx={{ textAlign: "left", flex: 1 }}>
            {quizCat.startsWith("Rad")
              ? (() => {
                  const strippedQuizCat = quizCat.replace("Rad", "").trim(); // Remove "Rad"
                  const baseCategory = strippedQuizCat.includes("99")
                    ? `${strippedQuizCat.split(":")[0]}+`
                    : strippedQuizCat.split(":")[0] === strippedQuizCat.split("-")[1]
                      ? strippedQuizCat.split(":")[0]
                      : strippedQuizCat;

                  return `${baseCategory} ${quizCat === "1:1" ? "Use" : "Uses"}`;
                })()
              : quizCat.startsWith("Hsk2") || quizCat.startsWith("Hsk3")
                ? (() => {
                    const hskNumber = quizCat.charAt(3); // Get the number (2 or 3)
                    const remainder = quizCat.substring(4); // Get everything after Hsk2/Hsk3
                    return `HSK${hskNumber} ${remainder.charAt(0).toUpperCase()}${remainder.slice(1)}`;
                  })()
                : quizCat.startsWith("Paronym")
                  ? `Paronym : ${quizCat.substring(7)}`
                  : quizCat}
          </Typography>
          {currentUser.timer > 0 && (
            <Typography
              variant="body1"
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                textAlign: "center",
                fontSize: "1.25rem",
                fontWeight: "bold",
                color: getTimerColor(),
              }}
            >
              00:{formatTime(remainingTime)}
            </Typography>
          )}
          <Typography variant="body1" sx={{ textAlign: "right", flex: 1 }}>
            {quizCat !== "Priority" && !quizCat.startsWith("LowScore") && !quizCat.startsWith("HSK")
              ? `${quizScore.average_score ? `${Math.round(quizScore.average_score)}%` : "0%"} ~ `
              : ""}
            {quizQuestions.length} rem
          </Typography>
        </Box>

        {/* Question and Answer Box */}
        {/* Flip Container */}
        <Box
          onClick={() => {
            if (isSoundChoiceQuiz && quizCat !== "radMini") {
              handleFlip(quizQuestions[0]?.hanzi_character, quizQuestions[0]?.hanzi_type);
              setSoundScore(0);
            } else if (quizCat !== "radMini") {
              handleFlip(quizQuestions[0]?.hanzi_character, quizQuestions[0]?.hanzi_type);
            } else {
              handleFlip(quizQuestions[0]?.hanzi_char_full, quizQuestions[0]?.hanzi_type);
            }
          }}
          sx={{
            width: "100%",
            height: 200,
            border: "1px solid #ccc",
            borderRadius: "18px",
            perspective: "1000px", // Create 3D perspective for flip effect
            // overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              border: "2px solid #000",
              borderRadius: "18px",
              bgcolor: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: 4,
              boxSizing: "border-box", // Ensure inner box respects dimensions
              transformStyle: "preserve-3d",
              transition: disableAnimation ? "none" : "transform 0.6s",
              transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)", // Rotate based on flip state
            }}
          >
            {/* Front Face */}
            <Box
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                backfaceVisibility: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box", // Ensure inner box respects dimensions
              }}
            >
              {/* Upper Left Indicators */}
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  left: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  fontSize: "14px",
                }}
              >
                {quizQuestions[0]?.hanzi_proper === 1 && <Box>NS</Box>}
                {quizQuestions[0]?.hanzi_proper === 2 && <Box>NG</Box>}
                {quizQuestions[0]?.hanzi_regional === "N" && <Box>RN</Box>}
                {quizQuestions[0]?.hanzi_regional === "S" && <Box>RS</Box>}
                {quizQuestions[0]?.hanzi_type > 2 && quizQuestions[0]?.hanzi_speech > 0 && (
                  <Box>P{quizQuestions[0]?.hanzi_speech}</Box>
                )}
              </Box>

              {/* Upper Right Indicators */}
              {!cardLoading && (
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: quizType === "MeaPin" ? "20px" : "24px",
                    fontWeight: 300,
                    fontFamily:
                      quizType === "HanPin"
                        ? '"Hiragino Mincho ProN", "Songti SC", "Noto Serif SC", serif'
                        : quizType === "PinHan"
                          ? '"Spectral"'
                          : "Roboto, Helvetica, Arial, sans-serif",
                    px:
                      quizType === "HanPin" && quizQuestions[0]?.hanzi_character.length > 5 ? 4 : 0,
                  }}
                >
                  {quizType === "HanPin" ? (
                    <Box display="flex" flexWrap="wrap" gap={0} justifyContent="center">
                      {quizQuestions[0].hanzi_character
                        .replace(/\s/g, "")
                        .split("")
                        .map((char, index, array) => (
                          <React.Fragment key={index}>
                            {cardFace === 1 ? (
                              <HanziCharacter
                                character={char}
                                width={array.length > 7 ? 64 : array.length > 3 ? 72 : 96}
                                height={array.length > 7 ? 64 : array.length > 3 ? 72 : 96}
                              />
                            ) : (
                              <Typography
                                sx={{
                                  fontWeight: 300,
                                  fontSize:
                                    quizQuestions[0]?.hanzi_character?.length > 10
                                      ? "28px"
                                      : quizQuestions[0]?.hanzi_character?.length > 9
                                        ? "30px"
                                        : quizQuestions[0]?.hanzi_character?.length > 7
                                          ? "32px"
                                          : quizQuestions[0]?.hanzi_character?.length > 4
                                            ? "40px"
                                            : quizQuestions[0]?.hanzi_character?.length > 3
                                              ? "48px"
                                              : "56px",
                                }}
                              >
                                {char}
                              </Typography>
                            )}
                            {/* Insert "or" between type=1 (radicals) with multiple characters */}
                            {quizQuestions[0].hanzi_type === 1 &&
                              array.length > 1 &&
                              index < array.length - 1 && (
                                <Box
                                  sx={{
                                    alignSelf: "center",
                                    mx: 0.5,
                                    fontWeight: "bold",
                                    fontSize: "1rem",
                                  }}
                                >
                                  or
                                </Box>
                              )}
                          </React.Fragment>
                        ))}
                    </Box>
                  ) : quizType === "MeaPin" ? (
                    <>
                      {!quizCat.startsWith("PhraseHsk") && quizMeaning}
                      <Typography
                        variant={quizCat.startsWith("PhraseHsk") ? "body1" : "body2"}
                        sx={{ mt: 1, textAlign: "center" }}
                      >
                        ({quizQuestions[0]?.hanzi_character.length} Character
                        {quizQuestions[0]?.hanzi_character.length > 1 ? "s" : ""})
                      </Typography>
                    </>
                  ) : quizType === "LisPin" ? (
                    <>
                      Listen
                      <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                        Tap the card or audio button to replay.
                      </Typography>
                    </>
                  ) : quizType === "MeaHan" ? (
                    <>
                      {!quizCat.startsWith("PhraseHsk") && quizMeaning}
                      <Typography
                        variant={quizCat.startsWith("PhraseHsk") ? "body1" : "body2"}
                        sx={{ mt: 1, textAlign: "center" }}
                      >
                        (Character {meaHanIndex} of{" "}
                        {quizQuestions[0]?.hanzi_type === 1
                          ? 1
                          : quizQuestions[0]?.hanzi_character.length}
                        )
                      </Typography>
                    </>
                  ) : (
                    quizType === "PinHan" &&
                    pinyinWithHighlight.map(({ text, bold }, index) => (
                      <span
                        key={index}
                        style={{
                          fontWeight: bold ? "normal" : "normal",
                          textDecoration: bold ? "underline" : "none",
                        }}
                      >
                        {text}
                        {index < pinyinWithHighlight.length - 1 && " "}{" "}
                        {/* Add space between words */}
                      </span>
                    ))
                  )}
                </Typography>
              )}
            </Box>

            {/* Back Face */}
            <Box
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                backfaceVisibility: "hidden",
                transform: "rotateY(180deg)", // Rotate content to back
                display: "flex",
                flexDirection: "column", // Arrange top and bottom sections vertically
                justifyContent: "space-between", // Space top and bottom sections
                alignItems: "center",
                boxSizing: "border-box",
                pl: 1,
                pr: 1,
              }}
            >
              {/* Upper Left Indicators */}
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  left: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  fontSize: "14px",
                }}
              >
                {quizQuestions[0]?.hanzi_proper === 1 && <Box>NS</Box>}
                {quizQuestions[0]?.hanzi_proper === 2 && <Box>NG</Box>}
              </Box>

              {/* Upper Right Indicators */}
              <Box
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  fontSize: "14px",
                }}
              >
                {quizQuestions[0]?.hanzi_regional === "N" && <Box>RN</Box>}
                {quizQuestions[0]?.hanzi_regional === "S" && <Box>RS</Box>}
                {quizQuestions[0]?.hanzi_type > 2 && quizQuestions[0]?.hanzi_speech > 0 && (
                  <Box>P{quizQuestions[0]?.hanzi_speech}</Box>
                )}
              </Box>
              {/* Top Section */}
              <Box
                sx={{
                  width: "100%",
                  textAlign: "center",
                }}
              >
                {/* Box to contain hanzi character once or twice in two fonts */}
                <Box
                  sx={{
                    display: quizQuestions[0]?.hanzi_character?.length < 4 ? "flex" : "block",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  {/* If Character QuizType and length < 4 */}
                  {quizQuestions[0]?.hanzi_character?.length < 4 ? (
                    <>
                      {/* Left Block - Serif Font */}
                      <Box
                        sx={{
                          width: "50%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily:
                              '"Hiragino Mincho ProN", "Songti SC", "Noto Serif SC", serif',
                            fontSize:
                              quizQuestions[0]?.hanzi_character?.length === 3 ? "48px" : "56px",
                            fontWeight: 300,
                            mb: quizQuestions[0]?.hanzi_character?.length === 3 ? 0.5 : -1,
                            padding: 0,
                          }}
                        >
                          {quizQuestions[0]?.hanzi_character}
                        </Typography>
                      </Box>
                      {/* Right Block - Sans-Serif Font */}
                      <Box
                        sx={{
                          width: "50%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: '"Arial", sans-serif',
                            fontSize:
                              quizQuestions[0]?.hanzi_character?.length === 3 ? "48px" : "56px",
                            fontWeight: 300,
                            mb: quizQuestions[0]?.hanzi_character?.length === 3 ? 0.5 : -1,
                            padding: 0,
                          }}
                        >
                          {quizQuestions[0]?.hanzi_character}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* If Character QuizType and length >= 4, display only one hanzi font */}
                      <Typography
                        variant="body1"
                        sx={{
                          // fontFamily: '"Hiragino Mincho ProN", "Songti SC", "Noto Serif SC", serif',
                          fontFamily: '"Arial", sans-serif',
                          fontSize:
                            quizQuestions[0]?.hanzi_character?.length > 10
                              ? "28px"
                              : quizQuestions[0]?.hanzi_character?.length > 9
                                ? "30px"
                                : quizQuestions[0]?.hanzi_character?.length > 7
                                  ? "32px"
                                  : quizQuestions[0]?.hanzi_character?.length > 4
                                    ? "40px"
                                    : quizQuestions[0]?.hanzi_character?.length > 3
                                      ? "48px"
                                      : "56px",
                          fontWeight: 300,
                          mb: -1,
                          padding: 0,
                        }}
                      >
                        {quizQuestions[0]?.hanzi_character}
                      </Typography>
                    </>
                  )}
                </Box>
                <Typography
                  variant="body1"
                  onClick={
                    audioPlaying
                      ? null
                      : () =>
                          quizCat !== "radMini"
                            ? handlePlayAudio(
                                quizQuestions[0]?.hanzi_character,
                                quizQuestions[0]?.hanzi_type,
                              )
                            : handlePlayAudio(
                                quizQuestions[0]?.hanzi_char_full,
                                quizQuestions[0]?.hanzi_type,
                              )
                  }
                  sx={{
                    fontFamily: "Spectral",
                    fontSize: quizQuestions[0]?.hanzi_pinyin.length > 24 ? "24px" : "28px",
                    fontWeight: 300,
                    color: "primary.main",
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": {
                      color: "primary.dark", // Change color on hover for better feedback
                    },
                    opacity: audioPlaying ? 0.7 : 1,
                    mt:
                      quizQuestions[0]?.hanzi_character?.length > 6
                        ? 2
                        : quizQuestions[0]?.hanzi_character?.length > 4
                          ? 2
                          : 0,
                    mb:
                      quizQuestions[0]?.hanzi_character?.length > 6
                        ? 2
                        : quizQuestions[0]?.hanzi_character?.length > 4
                          ? 2
                          : 1,
                    pl: quizQuestions[0]?.hanzi_pinyin.length > 24 ? 3 : 0,
                    pr: quizQuestions[0]?.hanzi_pinyin.length > 24 ? 3 : 0,
                    lineHeight: quizQuestions[0]?.hanzi_pinyin.length > 24 ? 0.9 : 1,
                  }}
                >
                  {!isSoundChoiceQuiz
                    ? quizQuestions[0]?.hanzi_pinyin
                    : pinyinWithHighlight.map(({ text, bold }, index) => (
                        <span
                          key={index}
                          style={{
                            fontWeight: bold ? "normal" : "normal",
                            textDecoration: bold ? "underline" : "none",
                          }}
                        >
                          {text}
                          {index < pinyinWithHighlight.length - 1 && " "}{" "}
                          {/* Add space between words */}
                        </span>
                      ))}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Arial",
                    fontSize: "20px",
                    fontWeight: 300,
                    mt: 1,
                    mb: 0,
                    pt: quizMeaning && quizMeaning.length > 38 ? 0 : 1,
                    pl: quizMeaning && quizMeaning.length > 38 ? 3 : 0,
                    pr: quizMeaning && quizMeaning.length > 38 ? 3 : 0,
                    lineHeight: quizMeaning && quizMeaning.length > 38 ? 0.9 : 1,
                  }}
                >
                  {quizMeaning}
                </Typography>
              </Box>

              {/* Bottom Section */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between", // Space items evenly
                }}
              >
                {/* Each Box takes up one-third of the width */}
                <Box
                  sx={{
                    width: quizQuestions[0]?.hanzi_type > 2 ? "33.33%" : "25%",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "Arial",
                      fontSize: "16px",
                      // fontWeight: quizQuestions[0]?.hanzi_hsk_comp < 9 ? 400 : 300,
                      // color: quizQuestions[0]?.hanzi_hsk_comp < 9 ? "orange" : "default",
                      color: "default",
                    }}
                  >
                    HSK:{" "}
                    {quizQuestions[0]?.hanzi_hsk_level === 9
                      ? "-"
                      : quizQuestions[0]?.hanzi_hsk_level}{" "}
                    /&nbsp;
                    {quizQuestions[0]?.hanzi_hsk_comp === 9
                      ? "-"
                      : quizQuestions[0]?.hanzi_hsk_comp}
                  </Typography>
                </Box>
                {/* For type > 2, Frequency is in BBs */}
                {quizQuestions[0]?.hanzi_type < 3 ? (
                  <Box
                    sx={{
                      width: quizQuestions[0]?.hanzi_type > 2 ? "33.33%" : "25%",
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Arial",
                        fontSize: "16px",
                        fontWeight: 300,
                      }}
                    >
                      Fq: {quizQuestions[0]?.hanzi_freq}
                    </Typography>
                  </Box>
                ) : null}
                <Box
                  sx={{
                    width: quizQuestions[0]?.hanzi_type > 2 ? "33.33%" : "25%",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "Arial",
                      fontSize: "16px",
                      fontWeight: 300,
                    }}
                  >
                    Ci:{" "}
                    {quizQuestions[0]?.hanzi_ci_course === 9
                      ? "-"
                      : `${quizQuestions[0]?.hanzi_ci_course}:${quizQuestions[0]?.hanzi_ci_lesson}`}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: quizQuestions[0]?.hanzi_type > 2 ? "33.33%" : "30%",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "Arial",
                      fontSize: quizQuestions[0]?.hanzi_dom_lesson.length > 1 ? "15px" : "16px",
                      fontWeight: 300,
                    }}
                  >
                    Dom:{" "}
                    {quizQuestions[0]?.hanzi_dom_course === 9
                      ? "-"
                      : `${quizQuestions[0]?.hanzi_dom_course}:${quizQuestions[0]?.hanzi_dom_chapter}:${quizQuestions[0]?.hanzi_dom_lesson}`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: "100%", pt: 1 }}>
          {/* Function Buttons */}
          <Grid container spacing={1}>
            <Grid item xs={2}>
              <Box
                onClick={flagging ? handleFlagIconFlag : handleFlagIconOpen}
                sx={{
                  width: "100%",
                  height: "50px",
                  bgcolor: (theme) => theme.palette.grey[300],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: (theme) =>
                    flagFlag > 0 ? theme.palette.error.main : theme.palette.grey[700],
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                <Flag />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box
                onClick={!isSoundChoiceQuiz ? () => setEnableAnswerBoxes(true) : null}
                sx={{
                  width: "100%",
                  height: "50px",
                  bgcolor: (theme) => theme.palette.grey[300],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: (theme) => theme.palette.grey[700],
                  borderRadius: "4px",
                  cursor: isSoundChoiceQuiz ? "not-allowed" : "pointer",
                  opacity: isSoundChoiceQuiz ? 0.5 : 1,
                }}
              >
                <FlashOn />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box
                onClick={handleCommentIconOpen}
                sx={{
                  width: "100%",
                  height: "50px",
                  bgcolor: (theme) => theme.palette.grey[300],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: getCommentButtonColor(),
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                <Comment />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box
                onClick={() => {
                  if (audioPlaying) {
                    return;
                  }
                  if (flippedOnce) {
                    setFlipped((prev) => !prev);
                    return;
                  }
                  if (quizCat !== "radMini") {
                    handlePlayAudio(
                      quizQuestions[0]?.hanzi_character,
                      quizQuestions[0]?.hanzi_type,
                    );
                  } else {
                    handlePlayAudio(
                      quizQuestions[0]?.hanzi_char_full,
                      quizQuestions[0]?.hanzi_type,
                    );
                  }
                }}
                sx={{
                  width: "100%",
                  height: "50px",
                  bgcolor: (theme) => theme.palette.grey[300],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: (theme) => theme.palette.grey[700],
                  borderRadius: "4px",
                  // cursor: flippedOnce ? "pointer" : "not-allowed",
                  // opacity: flippedOnce ? 1 : 0.5,
                  cursor: "pointer",
                  opacity: 1,
                }}
              >
                <Undo />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box
                onClick={
                  priorityId > 0 ? () => setShowPriorityActions((prev) => !prev) : handlePriority
                }
                sx={{
                  width: "100%",
                  height: "50px",
                  bgcolor: (theme) => theme.palette.grey[300],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: (theme) =>
                    priorityId > 0 ? theme.palette.error.main : theme.palette.grey[700],
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                <PriorityHigh />
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Box
                onClick={async () => {
                  try {
                    await axiosPrivate.get(
                      `/quizExit?user=${currentUser.id}&cat=${quizCat}&type=${quizType}&remaining=${quizQuestions.length}`,
                    );
                    // After axios request is completed, navigate back
                    if (quizCat.startsWith("Duo")) {
                      const sectionMatch = quizCat.match(/^Duo(\d+):/);
                      const section = sectionMatch ? sectionMatch[1] : null;
                      if (section) {
                        console.log("Navigating to Duo section:", section);
                        navigate("/quiz/duolingo", { state: { section } });
                        return;
                      }
                    }
                    if (quizCat.startsWith("Dom")) {
                      const courseMatch = quizCat.match(/^Dom(\d+):/);
                      const course = courseMatch ? courseMatch[1] : null;
                      if (course) {
                        console.log("Navigating to Dom course:", course);
                        navigate("/quiz/domino", { state: { course } });
                        return;
                      }
                    }
                    if (quizCat.startsWith("Ci")) {
                      const courseMatch = quizCat.match(/^Ci(\d+):/);
                      const course = courseMatch ? courseMatch[1] : null;
                      if (course) {
                        console.log("Navigating to Confucius course:", course);
                        navigate("/quiz/confucius", { state: { course } });
                        return;
                      }
                    }
                    navigate(-1);
                  } catch (error) {
                    console.error("Error submitting request:", error);
                  }
                }}
                sx={{
                  width: "100%",
                  height: "50px",
                  bgcolor: (theme) => theme.palette.grey[300],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: (theme) => theme.palette.grey[700],
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                <Cancel />
              </Box>
            </Grid>
          </Grid>

          {showPriorityActions && priorityId > 0 ? (
            <Grid container spacing={1} sx={{ mt: 0 }}>
              {["2d", "2w", "2m", "Off"].map((label) => (
                <Grid item xs={3} key={label}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() =>
                      label === "Off" ? handlePriority() : handleSnoozePriority(label)
                    }
                    sx={{ textTransform: "none", mt: 1 }}
                  >
                    {label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          ) : null}

          {/* Input Field */}
          <Box sx={{ mt: 1, pl: 2, pr: 2 }}>
            {flagging ? (
              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Enter Flag Note"
                  value={flagNote}
                  onChange={(e) => setFlagNote(e.target.value)}
                  inputProps={{ maxLength: 500 }}
                  inputRef={flagTextFieldRef}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: (theme) => theme.palette.error.main }, // Red outline for flagging mode
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleFlagSave(flagId > 0 ? "update" : "add")}
                >
                  Save
                </Button>
              </Box>
            ) : commenting ? (
              <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Hanzi Note"
                    value={hanziNote}
                    onChange={(e) => setHanziNote(e.target.value)}
                    inputProps={{ maxLength: 500 }}
                    inputRef={hanziNoteRef}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#2196f3" },
                      },
                    }}
                  />
                  <Button variant="outlined" color="primary" onClick={handleNoteSave}>
                    Save
                  </Button>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Meaning Hint"
                    value={meaningHint}
                    onChange={(e) => setMeaningHint(e.target.value)}
                    inputProps={{ maxLength: 500 }}
                    inputRef={meaningHintRef}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#ff9800" },
                      },
                    }}
                  />
                  <Button variant="outlined" color="primary" onClick={handleHintSave}>
                    Save
                  </Button>
                </Box>
              </Box>
            ) : !isSoundChoiceQuiz && clickMode === 0 ? (
              <TextField
                fullWidth
                variant="outlined"
                label="Enter Pinyin Text"
                inputProps={{
                  autoCapitalize: "none",
                  spellCheck: false,
                  autoComplete: "off",
                  autoCorrect: "off",
                  "data-form-type": "other",
                  "aria-autocomplete": "none",
                }}
                autoComplete="off" // Add this at TextField level as well
                value={pinyinInput}
                onChange={(event) => {
                  setPinyinInput(event.target.value); // Update state on input change
                  handlePinyinInput(event); // Existing logic
                }}
                onFocus={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                inputRef={pinyinTextFieldRef}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: (theme) =>
                        pinyinMatch
                          ? theme.palette.success.main
                          : pinyinLetterMatch
                            ? theme.palette.warning.main
                            : theme.palette.info.main, // Outline color
                    },
                    "&:hover fieldset": {
                      borderColor: (theme) =>
                        pinyinMatch
                          ? theme.palette.success.main
                          : pinyinLetterMatch
                            ? theme.palette.warning.main
                            : theme.palette.info.main, // Outline color on hover
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: (theme) =>
                        pinyinMatch
                          ? theme.palette.success.main
                          : pinyinLetterMatch
                            ? theme.palette.warning.main
                            : theme.palette.info.main, // Outline color when focused
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderWidth: pinyinMatch ? "3px !important" : "2px !important",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: (theme) =>
                      pinyinMatch
                        ? theme.palette.success.main
                        : pinyinLetterMatch
                          ? theme.palette.warning.main
                          : theme.palette.info.main, // Label color
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: (theme) =>
                      pinyinMatch
                        ? theme.palette.success.main
                        : pinyinLetterMatch
                          ? theme.palette.warning.main
                          : theme.palette.info.main, // Label color when focused
                  },
                  mt: 1,
                }}
              />
            ) : null}
          </Box>

          {/* Answer Buttons */}
          <Box sx={{ width: "100%", pt: 1 }}>
            <Grid container spacing={1}>
              {isSoundChoiceQuiz
                ? shuffledSoundAnswers.map((answerObj, index) => (
                    <Grid item xs={2} key={index}>
                      <Box
                        onClick={
                          // If flipped, any button advances to next question
                          flippedOnce
                            ? // Already answered correctly or 3 incorrect attempts > advance to next question
                              () => handleAnswer(soundScore)
                            : // : // If correct answer already selected or 3 incorrect attempts, disable button
                              // selectedCorrectSoundAnswer || incorrectSoundAnswers.includes(answerObj.character)
                              // ? null
                              () => handleSoundAnswer(answerObj.character, answerObj.type)
                        }
                        disabled={audioPlaying}
                        sx={{
                          width: "100%",
                          height: "50px",
                          bgcolor: (theme) =>
                            selectedCorrectSoundAnswer === answerObj.character
                              ? theme.palette.success.main
                              : incorrectSoundAttempts > 0 &&
                                  incorrectSoundAnswers.includes(answerObj.character)
                                ? theme.palette.error.main
                                : "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          borderRadius: "6px",
                          cursor: flippedOnce
                            ? "pointer"
                            : selectedCorrectSoundAnswer ||
                                incorrectSoundAnswers.includes(answerObj.character)
                              ? "not-allowed"
                              : "pointer",
                          opacity: incorrectSoundAnswers.includes(answerObj.character) ? 0.5 : 1,
                          // border:
                          //   selectedCorrectSoundAnswer === answerObj.character ||
                          //   (answerObj.character === quizQuestions[0]?.hanzi_character &&
                          //     incorrectSoundAttempts >= 3) ||
                          //   (incorrectSoundAttempts > 0 && incorrectSoundAnswers.includes(answerObj.character))
                          //     ? "2px solid"
                          //     : "none",
                          // borderColor: (theme) =>
                          //   selectedCorrectSoundAnswer === answerObj.character
                          //     ? theme.palette.success.main
                          //     : answerObj.character === quizQuestions[0]?.hanzi_character && incorrectSoundAttempts >= 3
                          //     ? theme.palette.success.main
                          //     : incorrectSoundAttempts > 0 && incorrectSoundAnswers.includes(answerObj.character)
                          //     ? theme.palette.error.main
                          //     : "transparent",
                        }}
                      >
                        <Typography variant="h6" sx={{ fontSize: "32px", fontWeight: 300 }}>
                          {answerObj.character}
                        </Typography>
                      </Box>
                    </Grid>
                  ))
                : // "一", "二", "三", "四", "五", "六"
                  ["零", "二十", "四十", "六十", "八十", "百"].map((char, index) => (
                    <Grid item xs={2} key={index}>
                      <Box
                        onClick={enableAnswerBoxes ? () => handleAnswer(index + 1) : null}
                        sx={{
                          width: "100%",
                          height: "50px",
                          bgcolor: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          borderRadius: "6px",
                          fontSize: char.length === 2 ? "24px" : "30px",
                          cursor: enableAnswerBoxes ? "pointer" : "not-allowed",
                          opacity: enableAnswerBoxes ? 1 : 0.5,
                          transition: "all 0.3s ease",
                        }}
                      >
                        {char}
                      </Box>
                    </Grid>
                  ))}
              {isSoundChoiceQuiz && flippedOnce ? (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ textAlign: "center" }}>
                    Click any blue box to advance to the next question.
                  </Typography>
                </Grid>
              ) : null}
            </Grid>
          </Box>
          {showHint && (
            <Box
              sx={{
                mt: 1,
                p: 2,
                bgcolor: (theme) => theme.palette.darkPaper,
                borderRadius: 1,
                boxShadow: 1,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                Meaning Hint:
              </Typography>
              <Typography variant="body1">{quizQuestions[0]?.hanzi_meaning_hint}</Typography>
            </Box>
          )}
        </Box>
      </Box>
      {/* Similar Sound Modal */}
      <Modal open={simSoundModalOpen} onClose={() => setSimSoundModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "75%", md: 600 },
            maxWidth: "600px",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: { xs: 1, sm: 3 },
            borderRadius: 2,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Similar Sounds: {quizQuestions[0]?.hanzi_speech_part3}
          </Typography>
          {simSoundData.map((item, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              {renderSubParts(item)}
            </Box>
          ))}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button variant="contained" onClick={() => setSimSoundModalOpen(false)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
      <ModalUpp
        open={modalUppOpen}
        onClose={handleCloseModalUpp}
        dyzData={modalDyzData}
        partsData={modalPartsData}
        subPartsData={modalSubPartsData}
        usesCharData={modalUsesCharData}
        usesMCWData={modalUsesMCWData}
        phrasesData={modalPhrasesData}
        paronymData={modalParonymData}
        audioPlaying={audioPlaying}
        handlePlayAudio={handlePlayAudio}
        hanziId={modalUppData?.hanziId}
        type={modalUppData?.type}
        character={modalUppData?.character}
        ptext={modalUppData?.ptext}
        pinyin={modalUppData?.pinyin}
        measure={modalUppData?.measure}
        dyz={modalUppData?.dyz}
      />
      {flippedOnce ? (
        <Grid
          container
          spacing={0}
          sx={{
            mt: 2,
            mb: 4,
            alignSelf: "stretch",
            justifyContent: {
              xs: "center",
              lg: quizQuestions[0].hanzi_type === 4 ? "center" : "space-between",
            },
            // >> changed 4/19 trying to make it centered when hanzi_type = 4
            // justifyContent: quizCat !== "radMini" ? { xs: "center", md: "space-between" } : "center",
            alignItems: quizCat !== "radMini" ? "flex-start" : "center",
            height: "auto",
          }}
          justifyContent="space-between"
          alignItems="flex-start" // Align items to the top
        >
          {quizCat !== "radMini" && (
            <Grid
              item
              xs={12}
              // md={6} >>> changed 4/19 trying to make it centered when hanzi_type = 4
              // xl={4}
              md={6}
              // lg={quizQuestions[0].hanzi_type === 4 ? 8 : 6}
              // xl={quizQuestions[0].hanzi_type === 4 ? 8 : 6}
              sx={{
                display: "flex",
                flexDirection: "column",
                // alignItems: "center",
                alignItems: {
                  xs: "center",
                  md: quizQuestions[0].hanzi_type === 4 ? "center" : "flex-end",
                },
                // border: "1px solid success.main",
                height: "auto",
              }}
            >
              {/* Building Blocks Accordion */}
              <Box
                key={renderKey}
                sx={{
                  maxWidth: "600px",
                  width: "100%",
                  margin: {
                    xs: "0 auto",
                    lg: "0",
                  },
                  py: 0,
                  pl: { xs: 0, md: 2 },
                  pr: { xs: 0, md: 1 },
                  textAlign: {
                    xs: "center",
                    lg: "left",
                  },
                  // textAlign: { xs: "center", md: "right" },>> changed 4/19 trying to make it centered when hanzi_type = 4
                }}
              >
                {/* Meaning Hint */}
                {quizQuestions[0].hanzi_meaning_hint &&
                  quizQuestions[0].hanzi_meaning_hint.length > 0 && (
                    <Accordion
                      sx={{ mb: 2, bgcolor: (theme) => theme.palette.darkPaper }}
                      defaultExpanded
                    >
                      <AccordionSummary sx={{ mt: 0 }}>
                        <Box
                          sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                        >
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, textAlign: "left", mt: -2 }}
                          >
                            Meaning Hint:
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ py: 0, mt: -3 }}>
                        <Typography variant="body1" sx={{ textAlign: "left", ml: 2, mb: 1 }}>
                          {quizQuestions[0]?.hanzi_meaning_hint}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  )}
                {/* Measure Accordion */}
                {quizQuestions[0].hanzi_measure && quizQuestions[0].hanzi_measure.length > 0 && (
                  <Accordion
                    sx={{ mb: 2, bgcolor: (theme) => theme.palette.darkPaper }}
                    defaultExpanded
                  >
                    <AccordionSummary sx={{ mt: 0 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, textAlign: "left", mt: -2 }}
                        >
                          Measure
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ py: 0, mt: -3 }}>
                      <Typography variant="body1" sx={{ textAlign: "left", ml: 2, mb: 1 }}>
                        {quizQuestions[0].hanzi_measure.charAt(0).toUpperCase() +
                          quizQuestions[0].hanzi_measure.slice(1)}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}
                {/* DYZ Accordion (characters with >1 pronunciations) */}
                {dyzData.length > 0 && (
                  <Accordion
                    sx={{ mb: 2, bgcolor: (theme) => theme.palette.darkPaper }}
                    defaultExpanded
                  >
                    <AccordionSummary sx={{ mt: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          mt: -2,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                          多音字 - Multiple Pronunciations
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ py: 0, mt: -3 }}>
                      {dyzData.map((use, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          {renderParts(use)}
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}
                {quizQuestions[0].hanzi_note && quizQuestions[0].hanzi_note.length > 0 && (
                  <Accordion
                    sx={{ mb: 2, bgcolor: (theme) => theme.palette.darkPaper }}
                    defaultExpanded
                  >
                    <AccordionSummary sx={{ mt: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          mt: -2,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                          Usage Note
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ py: 0, mt: -3 }}>
                      <Typography
                        variant="body1"
                        sx={{ textAlign: "left", ml: 2, mb: 1 }}
                      ></Typography>
                      {quizQuestions[0].hanzi_note}
                    </AccordionDetails>
                  </Accordion>
                )}
                <Accordion sx={{ pb: 4 }} defaultExpanded>
                  <AccordionSummary sx={{ mt: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                        Building Blocks
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                        {partsData.length}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 0, my: -2 }}>
                    {partsData.map((part, index) =>
                      subPartsData[index]?.length > 0 ? ( // Only render the Accordion if there are subparts
                        <Accordion key={index} defaultExpanded sx={{ boxShadow: "none" }}>
                          <AccordionSummary sx={{ mt: -1, mb: 0, px: 0 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              {renderParts(part, true)}
                              {/* <Typography variant="body2" sx={{ textAlign: "right" }}>
                                {subPartsData[index]?.length || 0}
                              </Typography> */}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ py: 0, my: -2, mb: 0 }}>
                            {subPartsData[index].map((subPart, subIndex) => (
                              <Box key={subIndex}>{renderParts(subPart, true)}</Box>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      ) : (
                        <Box key={index}>
                          <AccordionSummary sx={{ mt: -1, mb: 0, px: 0 }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              {renderParts(part, false)}
                              {/* <Typography variant="body2" sx={{ textAlign: "right" }}>
                                0
                              </Typography> */}
                            </Box>
                          </AccordionSummary>
                        </Box>
                      ),
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Grid>
          )}
          <Grid
            item
            xs={12}
            md={6}
            // xl={4}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: quizCat !== "radMini" ? { xs: "center", md: "flex-start" } : "center",
              justifyContent: "center",
              // border: "1px solid success.main",
              height: "auto",
            }}
          >
            <Box
              key={renderKey}
              sx={{
                maxWidth: "600px",
                width: "100%",
                margin: 0,
                py: 0,
                pl: { xs: 0, md: 1 },
                pr: { xs: 0, md: 2 },
                textAlign: "left",
              }}
            >
              {/* Uses Char Accordion */}
              {quizQuestions[0].hanzi_type < 3 ? (
                Array.isArray(usesCharData) && usesCharData.length > 0 ? (
                  <Accordion sx={{ mt: { xs: 2, md: 0 }, mb: 2 }}>
                    <AccordionSummary sx={{ mt: 0 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                          Uses: Char
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                          {usesCharData.length}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ py: 0 }}>
                      {usesCharData.slice(0, 5).map((use, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          {renderParts(use)}
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <Accordion sx={{ my: { xs: 2, md: 0 } }}>
                    <AccordionSummary sx={{ mt: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          my: { xs: 2, md: 0 },
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                          Uses: Char
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                          0
                        </Typography>
                      </Box>
                    </AccordionSummary>
                  </Accordion>
                )
              ) : (
                // For multi-character records, display separate accordion for each character
                (() => {
                  const characters =
                    quizQuestions[0].hanzi_type === 1
                      ? [quizQuestions[0].hanzi_character.split(" ")[0].charAt(0)] // Type 1: use first character of first word
                      : quizQuestions[0].hanzi_character.split(""); // Type 3,4: use all characters
                  return characters.map((char, charIndex) => {
                    const charData = usesCharData[char] || [];
                    return charData.length > 0 ? (
                      <Accordion
                        key={charIndex}
                        sx={{ mt: charIndex === 0 ? { xs: 2, md: 0 } : 0, mb: 0 }}
                      >
                        <AccordionSummary sx={{ mt: 0 }}>
                          <Box
                            sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                          >
                            <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                              Uses Char: {char}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, textAlign: "right" }}
                            >
                              {charData.length}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ py: 0 }}>
                          {charData.slice(0, 5).map((use, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                              {renderParts(use)}
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    ) : (
                      <Accordion
                        key={charIndex}
                        sx={{ mt: charIndex === 0 ? { xs: 2, md: 0 } : 0, mb: 0 }}
                      >
                        <AccordionSummary sx={{ mt: 0 }}>
                          <Box
                            sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                          >
                            <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                              Uses Char: {char}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, textAlign: "right" }}
                            >
                              0
                            </Typography>
                          </Box>
                        </AccordionSummary>
                      </Accordion>
                    );
                  });
                })()
              )}

              {/* Uses MCW Accordion */}
              {usesMCWData.length > 0 && quizQuestions[0].hanzi_type < 3 ? (
                <Accordion sx={{ my: { xs: 2, md: 0 } }}>
                  <AccordionSummary sx={{ mt: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                        Uses: MCW
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                        {usesMCWData.length}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 0 }}>
                    {usesMCWData.slice(0, 5).map((use, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        {renderParts(use)}
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ) : usesMCWData.length > 0 && quizQuestions[0].hanzi_type > 2 ? (
                quizQuestions[0].hanzi_character.split("").map((char, charIndex) => {
                  const charRecords = usesMCWData.filter((item) =>
                    item.hanzi_character.includes(char),
                  );
                  return charRecords.length > 0 ? (
                    <Accordion key={charIndex} sx={{ mt: charIndex === 0 ? 2 : 0 }}>
                      <AccordionSummary sx={{ mt: 0 }}>
                        <Box
                          sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                            Uses MCW: {char}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                            {charRecords.length}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ py: 0 }}>
                        {charRecords.slice(0, 5).map((use, index) => (
                          <Box key={index} sx={{ mb: 2 }}>
                            {renderParts(use)}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ) : null;
                })
              ) : (
                <Accordion sx={{ my: 0 }}>
                  <AccordionSummary sx={{ mt: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        my: { xs: 2, md: 0 },
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                        Uses MCW
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                        0
                      </Typography>
                    </Box>
                  </AccordionSummary>
                </Accordion>
              )}

              {/* Phrases Accordion */}
              {phrasesData.length > 0 && quizQuestions[0].hanzi_type < 4 ? (
                <Accordion sx={{ my: 2 }} defaultExpanded>
                  <AccordionSummary sx={{ mt: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                        Phrases
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                        {phrasesData.length}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 0 }}>
                    {phrasesData.slice(0, 5).map((phrase, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        {renderPhrases(phrase)}
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ) : quizQuestions[0].hanzi_type < 4 ? (
                <Accordion sx={{ my: 2 }}>
                  <AccordionSummary sx={{ mt: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                        Phrases
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                        0
                      </Typography>
                    </Box>
                  </AccordionSummary>
                </Accordion>
              ) : null}

              {/* Paronym Accordion */}
              {paronymData.length > 0 && quizQuestions[0].hanzi_type < 3 ? (
                <Accordion sx={{ my: 2 }}>
                  <AccordionSummary sx={{ mt: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                        Paronyms
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                        {paronymData.length}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ py: 0 }}>
                    {paronymData.map((paronym, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        {renderParts(paronym)}
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ) : paronymData.length > 0 && quizQuestions[0].hanzi_type > 2 ? (
                (() => {
                  const characters = quizQuestions[0].hanzi_character.split("");
                  const pinyins = quizQuestions[0].hanzi_pinyin.split(" ");
                  const ptexts = quizQuestions[0].hanzi_ptext.split(" ");

                  return characters.map((char, charIndex) => {
                    const currentPtext = ptexts[charIndex]?.replace(/\d+$/, "") || "";
                    const matchingRecords = paronymData.filter(
                      (item) => item.hanzi_ptext.replace(/\d+$/, "") === currentPtext,
                    );

                    return matchingRecords.length > 0 ? (
                      <Accordion key={charIndex} sx={{ mt: charIndex === 0 ? 2 : 0 }}>
                        <AccordionSummary sx={{ mt: 0 }}>
                          <Box
                            sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                          >
                            <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                              Paronyms: {char} {pinyins[charIndex] || ""}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, textAlign: "right" }}
                            >
                              {matchingRecords.length}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ py: 0 }}>
                          {matchingRecords.map((paronym, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                              {renderParts(paronym)}
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    ) : null;
                  });
                })()
              ) : (
                <Accordion sx={{ my: 2 }}>
                  <AccordionSummary sx={{ mt: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, textAlign: "left" }}>
                        Paronyms
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                        0
                      </Typography>
                    </Box>
                  </AccordionSummary>
                </Accordion>
              )}
            </Box>
          </Grid>
        </Grid>
      ) : null}
    </Box>
  );
};

export default Quiz;
